import { singleQuery } from "api/graphqlTypes";
import restClient from "api/restClient";
import { MAX_LENGTH } from "components/Form/Form.utils";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import StatusBadge from "components/StatusBadge";
import {
    ActivityConfig,
    FeByType,
    FeConfigType,
    FeRecordType,
    FeSubrecordType,
    RecordId,
    RelationshipConfig,
    ResourceConfig,
} from "config/config";
import { applyIncomingReferenceFlagDefaults } from "components/Form/relationshipSubmitPayload";
import objectTemplateAttributeConfig from "config/resources/object_template_attribute";
import pluginActionConfig from "config/resources/plugin_action";
import rolePermissionConfig from "config/resources/role_permission";
import userGitPublicKeyConfig from "config/resources/user_git_public_key";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { getRecordLocation } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { valueAndSchema } from "containers/Resources/screens/ResourcesSingle/constants";
import {
    markSocketIntentionallyClosed,
    wasSocketIntentionallyClosed,
} from "containers/Resources/screens/ResourcesSingle/socketIntentionalClose";
import {
    hasSocketEverOpened,
    markSocketOpened,
} from "containers/Resources/screens/ResourcesSingle/socketFirstOpen";
import { storeWorkspace } from "containers/Workspaces/utils";
import dayjs from "dayjs";
import { FormApi } from "final-form";
import { OBJECT_TEMPLATE_ATTRIBUTE_DATA_TYPE } from "graphql/generated/enums";
import { Bundle, Project, Workspace } from "graphql/generated/graphql";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { AnyAction, Dispatch } from "redux";
import { actions as dataStoreActions } from "utils/DataStore/DataStore.reducer";
import { getWebsocketError } from "utils/NotificationManager/NotificationManager.selectors";
import { useVisibilityTabChange } from "utils/useVisibilityTabChange";
import { checkContainerItemType, getIri } from "utils/utils";
import { installResumeReconnect } from "utils/websocketResumeReconnect";

export const fetchSingleResourceData = ({
    dispatch,
    resourceName,
    iri,
    recordId,
    additionalVariables = {},
    by,
    additionalQueryFields,
}: {
    dispatch: Dispatch;
    resourceName: FeRecordType | FeSubrecordType;
    iri: string;
    recordId: RecordId;
    additionalVariables?: { [key: string]: any };
    by?: FeByType;
    additionalQueryFields?: string;
}) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_FETCHING_RESOURCE,
            promise: { resolve, reject },
            iri,
            resourceName,
            params: {
                query: singleQuery(resourceName, by, additionalQueryFields),
                variables: {
                    id: recordId,
                    by,
                    ...additionalVariables,
                },
                allow_deleted: true,
            },
        });
    });

export const getResourceDataInitialValues = (
    resourceConfig: ResourceConfig | ActivityConfig | RelationshipConfig,
    resourceData
) =>
    resourceConfig?.formDTO?.initialValueDTO && resourceData
        ? resourceConfig.formDTO.initialValueDTO(resourceData)
        : resourceData;

// Re-exported from a standalone module so it can be unit-tested without pulling
// in the heavy config/router import graph. See writeOnlyFields.ts.
export { stripUntouchedWriteOnlyFields } from "./writeOnlyFields";

interface SingleRecordFormProps {
    formData: any;
    resourceConfig: any;
    dispatch: Dispatch<AnyAction>;
    resourceName: FeConfigType;
    actionType?: string;
    additionalVariables?: { [key: string]: any };
    additionalQueryFields?: string;
    additionalParams?: { [key: string]: any };
    duplicateRecordId?: string;
    iri?: string;
    /**
     * The record as the user began editing it: the base of the three-way diff in
     * the stale-write conflict dialog. Omit to let the save saga read the cache.
     */
    baseRecord?: Record<string, unknown> | null;
}

export const submitSingleRecordForm = ({
    formData,
    resourceConfig,
    dispatch,
    resourceName,
    actionType = resourcesActions.START_UPDATE_RESOURCE,
    additionalVariables = {},
    additionalQueryFields,
    additionalParams,
    duplicateRecordId,
    iri,
    baseRecord,
}: SingleRecordFormProps) => {
    const submittableFormData = Object.assign({}, { ...formData, ...additionalVariables });
    try {
        new Set(resourceConfig?.form).forEach((field: any) => {
            if (field.submitDto && submittableFormData[field.name] !== undefined) {
                submittableFormData[field.name] = field.submitDto(submittableFormData[field.name]);
            }
            if (field.remappingSubmitDto) {
                field.remappingSubmitDto(submittableFormData[field.name]).forEach((mapping) => {
                    submittableFormData[mapping.key] = mapping.value;
                });
                delete submittableFormData[field.name];
            }
        });
    } catch (e) {
        NotificationService.error(
            <ExpandableNotification
                title="Please check your input"
                subtitle="Some values could not be processed"
                details={e?.toString()}
            />
        );
        console.error(e);
        return null;
    }
    return new Promise((resolve, reject) => {
        dispatch({
            type: actionType,
            promise: { resolve, reject },
            resourceName,
            variables: submittableFormData,
            additionalQueryFields,
            additionalParams,
            duplicateRecordId,
            iri,
            ...(baseRecord ? { baseRecord } : {}),
        });
    }).then((response) => {
        if (resourceName === "workspace") {
            const workspaceResponse = response as Workspace;
            storeWorkspace({ response: workspaceResponse, dispatch });
        }
        return response;
    });
};

interface SubmitRelationshipRecordFormProps {
    dispatch: Dispatch<AnyAction>;
    formData: any;
    recordId?: string;
    resourceName: FeConfigType;
    additionalData: { [key: string]: any };
    resourceConfig: any;
    setNestedFormSaveData?: (data: any) => void;
    newRecordId: string;
    additionalParams?: { commit: boolean };
}

export const submitRelationshipRecordForm = ({
    dispatch,
    formData,
    recordId,
    resourceName,
    additionalData,
    resourceConfig,
    setNestedFormSaveData,
    newRecordId,
    additionalParams,
}: SubmitRelationshipRecordFormProps) => {
    const { parentResourceType, parentResourceId } = additionalData;
    // Sanitise the meaningless flags of an INCOMING_REFERENCE attribute before
    // submit (the ObjectTemplateCanvas AttributeCard path submits full formData).
    // No-op for every other datatype / record type. (Stefan, 2026-10-12.)
    formData = applyIncomingReferenceFlagDefaults(formData, formData?.datatype);
    const actionType = recordId
        ? resourcesActions.START_UPDATE_RESOURCE
        : resourcesActions.START_CREATE_RESOURCE;

    const idField = recordId ? { id: recordId } : {};
    const parentTypeField =
        parentResourceType && parentResourceId
            ? { [`${parentResourceType}_id`]: parentResourceId }
            : {};
    const commonFields = { ...idField, ...parentTypeField };
    const pluginActionType = !recordId ? { type: "RECORD" } : {};

    const getAdditionalVariables = () => {
        switch (resourceName) {
            case userGitPublicKeyConfig.resourceName:
            case rolePermissionConfig.resourceName:
            case objectTemplateAttributeConfig.resourceName:
                return commonFields;
            case pluginActionConfig.resourceName:
                return { ...commonFields, ...pluginActionType };
            default:
                return {};
        }
    };

    return submitSingleRecordForm({
        formData,
        resourceConfig,
        dispatch,
        resourceName,
        actionType,
        additionalVariables: getAdditionalVariables(),
        additionalParams,
        iri: getIri(resourceName, recordId || newRecordId),
    })?.then(() => {
        return setNestedFormSaveData?.((prevData) => {
            delete prevData[newRecordId];
            return prevData;
        });
    });
};

export const getDateTimeAppendix = () => {
    const now = dayjs();
    const msec = now.format("SSS");
    return `${now.format("YYYY-MM-DD hh:mm:ss")} ${msec}`;
};

export const isMonacoEditorActiveElement = () =>
    document.activeElement?.tagName === "TEXTAREA" &&
    document.activeElement?.className.includes("monaco");

export const blurActiveElement = () => (document.activeElement as HTMLElement)?.blur();
export const blurActiveElementConditionally = (isConditionMet: boolean) => {
    if (isConditionMet) {
        blurActiveElement();
    }
};
export const blurCodeEditor = () => blurActiveElementConditionally(isMonacoEditorActiveElement());

export const onKeyDown = async (e: KeyboardEvent, formId: string, callback?: () => void) => {
    // TODO (medium) test on mac: we might need "navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey" or similar
    if (e.ctrlKey && e.key === "s") {
        e.preventDefault(); // prevent save dialogue
        await blurCodeEditor();
        await document.getElementById(formId)?.dispatchEvent(
            new Event("submit", {
                bubbles: true, // whether the event will bubble up through the DOM or not
                cancelable: true, // whether the event may be canceled or not
                // { cancelable: true } required for Firefox
                // https://github.com/facebook/react/issues/12639#issuecomment-382519193
            })
        );
        callback?.();
    }
};

interface SubscribeUpdatesProps {
    subscription: string;
    recordId: any;
    dispatch: Dispatch<AnyAction>;
    recordName: FeConfigType;
    iri: string;
    // The pushed record data is handed to the callback so consumers can compare it
    // against the current form baseline (per-field live-conflict detection) instead
    // of flagging every push unconditionally. Existing callers that ignore the arg
    // stay backward-compatible.
    callback?: (data?: any) => void;
    showErrorScreen: () => void;
    getSocket: (url: string, cacheKey?: string) => ReconnectingWebSocket | null;
    deleteSocket: (cacheKey: string) => void;
}

type SocketPoolEntry = {
    socket: ReconnectingWebSocket;
    refCount: number;
};

const socketPool = new Map<string, SocketPoolEntry>();

// Force a reconnect of every pooled socket. Used on tab resume to replace any
// "zombie" connection (dead but still reporting readyState OPEN, so the
// ReconnectingWebSocket never auto-reconnects) with a fresh one. See
// websocketResumeReconnect for the full rationale.
export const reconnectAllSockets = () => {
    socketPool.forEach((entry) => {
        try {
            entry.socket.reconnect();
        } catch (error) {
            // A failed forced reconnect on one socket must not break the resume
            // path for the others; the ReconnectingWebSocket keeps its own
            // retry loop regardless.
            console.error("Failed to force-reconnect websocket on resume", error);
        }
    });
};

const acquireSocket = (url: string, cacheKey: string) => {
    // Ensure the tab-resume reconnect handler is active whenever we have live
    // sockets (installed once, globally).
    installResumeReconnect(reconnectAllSockets);
    let entry = socketPool.get(cacheKey);
    if (!entry) {
        entry = { socket: new ReconnectingWebSocket(url), refCount: 0 };
        socketPool.set(cacheKey, entry);
    }
    entry.refCount += 1;
    return entry.socket;
};

const releaseSocket = (cacheKey: string) => {
    const entry = socketPool.get(cacheKey);
    if (!entry) {
        return;
    }
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
        // Mark before closing so the socket's own error/close listeners can tell
        // this app-initiated teardown apart from a genuine backend failure.
        markSocketIntentionallyClosed(entry.socket);
        entry.socket.close();
        socketPool.delete(cacheKey);
    }
};

export const useReconnectingWebsocket = () => {
    const { closeAllWebsocketConnections } = useVisibilityTabChange();
    const heldKeysRef = useRef(new Set<string>());

    // `cacheKey` identifies the cached socket. It defaults to the connection
    // URL, but callers whose connection URLs are identical (e.g. every GraphQL
    // subscription now connects to `.../graphql?subscription_message=true` and
    // sends its query in the first message) must pass a distinct key so their
    // sockets don't collapse into one. The key stays client-side only.
    const getSocket = useCallback((url: string, cacheKey = url) => {
        heldKeysRef.current.add(cacheKey);
        return acquireSocket(url, cacheKey);
    }, []);

    const deleteSocket = useCallback((cacheKey: string) => {
        if (!heldKeysRef.current.has(cacheKey)) {
            return;
        }
        heldKeysRef.current.delete(cacheKey);
        releaseSocket(cacheKey);
    }, []);

    const closeAll = useCallback(() => {
        Array.from(heldKeysRef.current).forEach((cacheKey) => {
            releaseSocket(cacheKey);
        });
        heldKeysRef.current.clear();
    }, []);

    useEffect(() => {
        if (closeAllWebsocketConnections) {
            closeAll();
        }
    }, [closeAllWebsocketConnections, closeAll]);

    useEffect(() => {
        const heldKeys = heldKeysRef.current;
        return () => {
            heldKeys.forEach((cacheKey) => releaseSocket(cacheKey));
            heldKeys.clear();
        };
    }, []);

    return { getSocket, deleteSocket };
};

/**
 * Whether SECONDARY live-data subscriptions (per-record updates, resource-stats
 * widgets, dependency visualization, dashboard-panel lists) should stay closed.
 *
 * Holds them closed while EITHER the tab has been backgrounded past the idle
 * timeout (existing tab-visibility behaviour) OR the primary `/ui` websocket is
 * down (an outage / mid-deploy backend). During a `/ui` outage each secondary
 * socket would otherwise keep its own ReconnectingWebSocket retry loop hammering
 * a backend that is known to be unreachable; gating the consuming subscribe
 * effects on this value tears the secondaries down and holds them closed, so
 * only the primary `/ui` socket keeps retrying. They re-subscribe automatically
 * once `/ui` is back, because the primary socket's recovery clears
 * `hasWebsocketError` and the gated effects re-run.
 *
 * Scoped to secondary sockets only: the primary `/ui` socket (MultiWebsocket)
 * must keep reconnecting — its reopen is what clears `hasWebsocketError` — so it
 * deliberately does NOT use this hold. Gating `/ui` on it would deadlock the
 * "connection lost" banner permanently.
 */
export const useSecondaryLiveDataHold = () => {
    const { closeAllWebsocketConnections } = useVisibilityTabChange();
    const hasWebsocketError = useSelector(getWebsocketError);
    return closeAllWebsocketConnections || hasWebsocketError;
};

/**
 * Subscribes to updates for a record (custom_object, execution)
 * @param param0 - The subscription, recordId, dispatch, recordName, iri, callback, showErrorScreen, getSocket, deleteSocket
 * @returns {void}
 */
export const subscribeUpdates = ({
    subscription,
    recordId,
    dispatch,
    recordName,
    iri,
    callback,
    showErrorScreen,
    getSocket,
    deleteSocket,
}: SubscribeUpdatesProps) => {
    // Defensive: never open a subscription before the record id is known. The
    // subscriptions require a String! $id; an absent recordId serialises to
    // variables:{} and the backend rejects the required-variable validation
    // (surfaced to the user as a spurious "Could not load live data" toast).
    // Callers should already guard this; this keeps the util safe regardless.
    if (!recordId) {
        return () => {};
    }
    const typedWindow: any = window;
    const websocketParams = {
        subscription,
        variables: {
            id: recordId,
        },
        allow_deleted: true,
    };

    const host = typedWindow.REACT_APP_WEBSOCKET_API || `wss://${window.location.host}`;
    const url = `${host}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql`;
    // Keep GraphQL out of the URL: connect to a clean endpoint and send the
    // subscription in the first websocket message. Cache the socket under a
    // per-record key (client-side only) so distinct records keep distinct
    // connections despite the shared connection URL.
    const socketUrl = `${url}?subscription_message=true`;
    const cacheKey = `${url}#${recordName}#${recordId}`;
    const socket = getSocket(socketUrl, cacheKey);
    // A pooled socket handed back already OPEN has opened before, even though our
    // freshly-added "open" listener below will not fire again for it: record that
    // so a later error on it is treated as a genuine mid-session drop, not a
    // suppressible first-connect transient.
    if (socket?.readyState === ReconnectingWebSocket.OPEN) {
        markSocketOpened(socket);
    }

    socket?.addEventListener("open", () => {
        markSocketOpened(socket);
        socket?.send(JSON.stringify(websocketParams));
    });

    socket?.addEventListener("error", (error) => {
        // Skip the toast when we ourselves tore this socket down (component
        // unmount / rapid back-button navigation / tab-hidden): closing a
        // still-connecting socket emits a native error event that is not a real
        // live-data failure.
        if (wasSocketIntentionallyClosed(socket)) {
            return;
        }
        // Skip the toast for an error on a socket that has never opened: this is a
        // transient first-connect failure during a cold SPA load (deep-link in a
        // fresh tab), which ReconnectingWebSocket retries and the "open" handler
        // above then re-sends the subscription. A genuine total outage is already
        // surfaced by the primary `/ui` "Connection to backend lost" banner, so
        // suppressing this secondary pre-open error hides nothing real. Once the
        // socket has opened at least once, a later error (a real mid-session
        // live-data drop) still toasts.
        if (!hasSocketEverOpened(socket)) {
            return;
        }
        console.error(error);
        // Surface the otherwise-silent connection failure -- but only while the
        // primary `/ui` socket is up (an isolated subscription failure). While
        // the primary socket is down, the top "Connection to backend lost"
        // banner already communicates the outage, so suppress this secondary
        // toast to stop the reconnect-storm repeating-toast defect.
        NotificationService.liveDataError(
            "Could not load live data. Check your connection or refresh the page.",
            { toastId: "record-live-updates-error" }
        );
    });

    socket?.addEventListener("message", (event) => {
        const eventData = JSON.parse(event.data);
        const data = eventData[recordName];
        if (eventData.errors != null) {
            if (eventData.errors.includes('GraphQLError("Could not find')) {
                deleteSocket(cacheKey);
                showErrorScreen();
            } else {
                NotificationService.error(
                    <ExpandableNotification
                        title="Error fetching live data"
                        details={`${eventData.errors}`}
                    />
                );
            }
        }
        // A failed subscribe is reported by the backend as an error envelope
        // keyed by the error class -- {"BadRequest": {title, message}} for a
        // client-side problem (e.g. a missing/invalid required variable) or
        // {"InternalServerError": {title, message}} for a server-side failure.
        // Surface the real server message instead of letting it fall through to
        // the generic "Could not load live data" connection toast (which comes
        // from the socket error/reconnect path). The shared toastId de-dupes
        // against that generic toast so only the more specific one is shown.
        const errorEnvelope = eventData.BadRequest || eventData.InternalServerError;
        if (errorEnvelope) {
            NotificationService.error(
                <ExpandableNotification
                    title={errorEnvelope.title || "Could not load live data"}
                    details={`${errorEnvelope.message ?? ""}`}
                />,
                { toastId: "record-live-updates-error" }
            );
        }
        if (data) {
            dispatch({
                type: resourcesActions.STORE_RESOURCE_DATA,
                recordName,
                iri,
                response: data,
            });
            callback?.(data);
        }
    });

    return () => {
        deleteSocket(cacheKey);
    };
};

export const fetchSchemaFormSchema = async (dispatch) => {
    const schemaResponse = await restClient.get("/formSchema.json");
    return new Promise((resolve, reject) => {
        dispatch({
            type: dataStoreActions.STORE_DATA,
            key: "schemaFormSchema",
            data: schemaResponse?.data,
        });
    }).catch((error) => error);
};

export const checkSSPHidden = () => (window as any).REACT_APP_SSP_ENABLED === false;

export const getReadonlyBadges = ({ resourceData, resourceConfig, size = "" }) => {
    if (resourceConfig?.ignoreIsReadonly) return;
    if (resourceData?.is_readonly) {
        return <StatusBadge type="primary-yellow" text="READ-ONLY" size={size} />;
    } else if (resourceData?.is_bundle_readonly) {
        return <StatusBadge type="primary-yellow" text="READ-ONLY BUNDLE" size={size} />;
    } else if (resourceData?.is_readonly) {
        return <StatusBadge type="primary-yellow" text="READ-ONLY" size={size} />;
    } else if (resourceData?.is_project_readonly) {
        return <StatusBadge type="primary-yellow" text="READ-ONLY PROJECT" size={size} />;
    }
};

export const getDeletedBadges = ({ resourceData, size = "" }) => {
    if (resourceData?.deleted_at) {
        return <StatusBadge type="primary-yellow" text="IN TRASH" size={size} />;
    }
    return null;
};

export const getPendingTrashBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.pending_trash_identity_id) {
        return <StatusBadge type="primary-yellow" text="PENDING MOVE TO TRASH" size={size} />;
    }
    return null;
};

export const getPendingRestoreBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.pending_restore_identity_id) {
        return <StatusBadge type="primary-yellow" text="PENDING RESTORE" size={size} />;
    }
    return null;
};

export const getPendingDeleteBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.pending_delete_identity_id) {
        return <StatusBadge type="primary-yellow" text="PENDING DELETE" size={size} />;
    }
    return null;
};

export const getDeprecationBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.has_deprecation) {
        return <StatusBadge type="primary-yellow" text="HAS DEPRECATION" size={size} />;
    }
};
export const getSyntaxErrorBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.has_syntax_error) {
        return <StatusBadge type="primary-red" text="SYNTAX ERROR" size={size} />;
    }
};

export const getBundleContentBadge = ({ resourceData, size = "" }) => {
    if (resourceData?.bundle_id && resourceData?.is_bundle_content) {
        return <StatusBadge type="primary-blue" text="BUNDLE CONTENT" size={size} />;
    }
};

export const getBootstrapUserBadge = ({ isBootstrapUser, size = "" }) => {
    if (isBootstrapUser) {
        return <StatusBadge type="primary-blue" text="BOOTSTRAP USER" size={size} />;
    }
};

export const setValueDependingOnDataType = (values) => {
    // reference_object_template_id is used by BOTH OBJECT_TEMPLATE_REFERENCE (the
    // referenced template) and INCOMING_REFERENCE (the referencing template, set
    // by the incoming-reference picker) — keep it for either.
    if (
        values.datatype !== "OBJECT_TEMPLATE_REFERENCE" &&
        values.datatype !== "INCOMING_REFERENCE"
    ) {
        values.reference_object_template_id = null;
    }
    if (values.datatype !== "INCOMING_REFERENCE") {
        values.incoming_reference_attribute = null;
    }
    if (values.datatype !== "RECORD_REFERENCE") {
        values.reference_record_type = null;
    }
    if (values.datatype !== "ALLOWED_VALUES") {
        values.allowed_values = null;
    }
};

export const filterObjectTemplateDataTypeList = (id) => {
    // TODO: this is a temporary fix to exclude certain types from the dropdown
    const objectTemplateAttributeDataTypeIndex = objectTemplateAttributeConfig.form?.findIndex(
        (config) => config?.fieldProps?.options?.enumName === "ObjectTemplateAttributeDataType"
    );

    if (
        objectTemplateAttributeDataTypeIndex !== undefined &&
        objectTemplateAttributeDataTypeIndex !== -1 &&
        objectTemplateAttributeConfig.form
    ) {
        objectTemplateAttributeConfig.form[
            objectTemplateAttributeDataTypeIndex
        ].fieldProps.options.list = OBJECT_TEMPLATE_ATTRIBUTE_DATA_TYPE.filter(
            (type) => type !== "BYTES"
        ).map((type) => ({
            label: type,
            value: type,
        }));

        objectTemplateAttributeConfig.form[
            objectTemplateAttributeDataTypeIndex
        ].fieldProps.customKey = `${id}-ObjectTemplateAttributeDataType`;

        delete objectTemplateAttributeConfig.form[objectTemplateAttributeDataTypeIndex].fieldProps
            .options.enumName;
    }
};

export const newRecordId = "new";

interface StoreFormDataProps {
    pristine: boolean;
    setNestedFormSaveData: (data: any) => void;
    recordId?: string;
    form: FormApi<any>;
    isNew: boolean;
    newRecordId: string;
    fetchResourceData?: () => Promise<any>;
    onConfirm?: () => void;
}

export const storeFormData = ({
    pristine,
    setNestedFormSaveData,
    recordId,
    form,
    isNew,
    newRecordId,
    fetchResourceData,
    onConfirm,
}: StoreFormDataProps) => {
    if (recordId) {
        if (pristine === false) {
            // to avoid looping wrap setState in setTimeout
            // https://github.com/final-form/react-final-form/issues/809
            setTimeout(
                () =>
                    setNestedFormSaveData((prevData) => ({
                        ...prevData,
                        [recordId]: form.submit,
                    })),
                0
            );
        } else if (pristine === true) {
            setTimeout(
                () =>
                    setNestedFormSaveData((prevData) => {
                        const changedData = { ...prevData };
                        delete changedData[recordId];
                        return changedData;
                    }),
                0
            );
        }
    } else if (isNew && pristine === true) {
        setTimeout(
            () =>
                setNestedFormSaveData((prevData) => ({
                    ...prevData,
                    [newRecordId]: () =>
                        form
                            .submit()
                            ?.then(() => fetchResourceData?.())
                            .then(() => onConfirm?.()),
                })),
            0
        );
    }
};

export const storeFormDataForCanvas = ({
    pristine,
    setNestedFormSaveData,
    recordId,
    form,
    isNew,
    newRecordId,
}: StoreFormDataProps) => {
    if (recordId) {
        if (pristine === false) {
            // to avoid looping wrap setState in setTimeout
            // https://github.com/final-form/react-final-form/issues/809
            setTimeout(
                () =>
                    setNestedFormSaveData((prevData) => ({
                        ...prevData,
                        [recordId]: form,
                    })),
                0
            );
        } else if (pristine === true) {
            setTimeout(
                () =>
                    setNestedFormSaveData((prevData) => {
                        const changedData = { ...prevData };
                        delete changedData[recordId];
                        return changedData;
                    }),
                0
            );
        }
    } else if (isNew && pristine === true) {
        setTimeout(
            () =>
                setNestedFormSaveData((prevData) => ({
                    ...prevData,
                    [newRecordId]: form,
                })),
            0
        );
    }
};

export const saveNestedForms = async (nestedFormSaveData) => {
    const saveNestedForms = Object.values(nestedFormSaveData)
        .filter((save) => save)
        .map((save: any) => save()?.then(() => true));
    if (saveNestedForms.length) {
        const result = await Promise.allSettled(saveNestedForms);
        const successfulResult = result.every(
            (item) => item.status === "fulfilled" && item.value === true
        );
        if (successfulResult) {
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    } else {
        return Promise.resolve();
    }
};

export const saveNestedFormsForCanvas = async ({
    nestedFormsData,
    setLoadingToFalse,
    fetchList,
}: {
    nestedFormsData: { [key: string]: any };
    setLoadingToFalse?: () => void;
    fetchList?: () => void;
}) => {
    const saveNestedForms = Object.entries(nestedFormsData).map(([key, form]) => {
        return form
            .submit()
            ?.then(() => {
                if (key !== newRecordId) {
                    form.restart(form.getState().values);
                }
                return true;
            })
            ?.catch(() => {
                setLoadingToFalse?.();
                return false;
            });
    });

    if (saveNestedForms.find((item) => item !== undefined)) {
        const result = await Promise.allSettled(saveNestedForms);
        const successfulResult = result.every(
            (item) => item.status === "fulfilled" && item.value === true
        );
        if (successfulResult) {
            fetchList?.();
            return Promise.resolve();
        } else {
            setLoadingToFalse?.();
            return Promise.reject();
        }
    } else {
        setLoadingToFalse?.();
        // if nestedFormsData is empty that means no error => resolve
        if (Object.keys(nestedFormsData).length === 0) {
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    }
};

export const changeCanCommitOnSaveMutator = async (args, state, utils) => {
    const newCommitOnSaveValue = args[0];
    await utils.changeValue(state, "canCommitOnSave", () => newCommitOnSaveValue);
};

export const changeBreakpointsMutator = (args, state, utils) =>
    utils.changeValue(state, "breakpoints", () => args[0]);

export const changeSchemaFormValues = (args, state, utils) =>
    utils.changeValue(state, valueAndSchema, () => args[0]);

export const changeScheduler = (args, state, utils) =>
    utils.changeValue(state, "scheduler_id", () => args[0]);

export const getQueryCommitParams = ({
    formData,
    form,
    canCommitOnSaveDefaultValue,
    canTrackInGit,
}) => {
    const canCommitOnSave =
        canTrackInGit === false
            ? false
            : canCommitOnSaveDefaultValue !== undefined &&
              form.getState().dirtyFields.canCommitOnSave !== undefined
            ? formData.canCommitOnSave
            : canCommitOnSaveDefaultValue;
    delete formData["canCommitOnSave"];
    const additionalParams = { commit: !!canCommitOnSave };

    return additionalParams;
};

export const getQueryMigrateOnSaveParams = (canMigrateOnSave) => {
    return { migrate: canMigrateOnSave };
};

export const resetFinalForm = (form) => {
    // reset + keepDirtyOnReinitialize don't work together
    // workaround https://github.com/final-form/final-form/issues/151#issuecomment-425867172
    if (form) {
        form.setConfig("keepDirtyOnReinitialize", false);
        form.reset();
        form.setConfig("keepDirtyOnReinitialize", true);
    }
};

export const canLoadFileKey = (iri) => `canLoad-${iri}`;

export const getAdditionalSubmitFormParams = (...args) => {
    const mergedParams = Object.assign({}, ...args.filter((arg) => arg));
    return Object.keys(mergedParams).length > 0 ? mergedParams : undefined;
};

export const getRecordContainer = (resourceData) => {
    if (!resourceData || checkContainerItemType(resourceData?.record_type)) {
        return { containerType: null, containerData: null };
    }
    const containerType: FeRecordType = getRecordLocation(resourceData);
    const containerData: Workspace | Project | Bundle =
        resourceData[`${containerType}_id_${containerType}`];
    return { containerType, containerData };
};

export const getDuplicatedName = (duplicatedName = "") => {
    const lastPart = ` copy at ${getDateTimeAppendix()}`;
    const allowedLength = MAX_LENGTH - lastPart.length;
    const nameLength = duplicatedName.length;
    let newName = duplicatedName;
    if (nameLength > allowedLength) {
        newName = duplicatedName.slice(0, allowedLength);
    }
    return `${newName}${lastPart}`;
};
